import { type CSSProperties, forwardRef } from 'react';
import { clsx } from 'clsx';
import styles from './ColorSwatch.module.css';

export interface ColorSwatchProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Hex color to display, or 'none' for the transparent/strikethrough cell */
  color: string | 'none';
  /** Whether this swatch is the currently selected color */
  active?: boolean;
  size?: 'sm' | 'md';
}

/**
 * ColorSwatch — a round color button used inside color picker grids.
 *
 * @example
 * <ColorSwatch color="#FEF9C3" active={value === '#FEF9C3'} onClick={() => onChange('#FEF9C3')} />
 * <ColorSwatch color="none" active={value === 'none'} onClick={() => onChange('none')} />
 */
export const ColorSwatch = forwardRef<HTMLButtonElement, ColorSwatchProps>(
  function ColorSwatch({ color, active = false, size = 'md', className, style, ...props }, ref) {
    const isNone = color === 'none';
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        aria-label={isNone ? 'No color' : color}
        title={isNone ? 'None' : color}
        className={clsx(styles.swatch, styles[size], isNone && styles.none, className)}
        style={
          isNone
            ? style
            : ({ '--swatch-color': color, ...style } as CSSProperties)
        }
        {...props}
      />
    );
  }
);
